import React, { useState, useCallback, useRef } from 'react';
import { Pressable, ScrollView, Image } from 'react-native';
import { XStack, YStack, Text, View, Separator } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useSelector } from 'react-redux';
import CalendarQRModal, { CalendarQRHandle } from './CalendarQRModal';

interface CalendarUser {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string;
}

interface Calendar {
  _id?: string;
  title?: string;
  name?: string; // Fallback for compatibility
  color?: string;
  description?: string;
  type?: string;
  isSelected?: boolean;
  calendarId?: string;
  social?: boolean;
  user?: CalendarUser; // User information (when in company context)
  [key: string]: any;
}

interface CalendarSelectionListProps {
  calendars: Calendar[];
  selectedCalendars: string[];
  onCalendarToggle: (calendarId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  isLoading?: boolean;
}

const CalendarSelectionList: React.FC<CalendarSelectionListProps> = ({
  calendars,
  selectedCalendars,
  onCalendarToggle,
  onSelectAll,
  onDeselectAll,
  isLoading = false
}) => {
  const theme = themeSelector();
  const [isExpanded, setIsExpanded] = useState(false);
  const qrModalRef = useRef<CalendarQRHandle>(null);
  const [selectedCalendar, setSelectedCalendar] = useState<Calendar | null>(null);

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleShowQR = useCallback((calendar: Calendar, e: any) => {
    e.stopPropagation(); // Prevent calendar toggle when clicking QR button
    setSelectedCalendar(calendar);
    const calendarId = calendar._id || calendar.calendarId || '';
    qrModalRef.current?.showModal(calendarId);
  }, []);

  const getCalendarColor = (calendar: Calendar) => {
    if (calendar.color) {
      // Handle colors with alpha values (e.g., "#009688ff" -> "#009688")
      return calendar.color.length === 9 ? calendar.color.slice(0, 7) : calendar.color;
    }

    // Default colors for different calendar types
    const defaultColors = {
      smartsaas: '#3b82f6',
      social: '#FF5733',
      work: '#10b981',
      personal: '#f59e0b',
      google: '#4285f4',
      outlook: '#0078d4',
      default: '#6b7280'
    };

    return defaultColors[calendar.type] || defaultColors.default;
  };

  const getCalendarIcon = (calendar: Calendar) => {
    const type = calendar.type?.toLowerCase();
    const isSocial = calendar.social || type === 'social';

    if (isSocial) {
      return 'share';
    }

    switch (type) {
      case 'smartsaas':
        return 'event-note';
      case 'google':
        return 'calendar-today';
      case 'outlook':
        return 'event';
      case 'work':
        return 'work';
      case 'personal':
        return 'person';
      default:
        return 'event-note';
    }
  };

  const getUserDisplayName = (user: CalendarUser | undefined) => {
    if (!user) return null;

    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) return user.firstName;
    if (user.lastName) return user.lastName;
    if (user.email) return user.email;

    return 'Unknown User';
  };

  const correctStorageUrl = (url: string | undefined) => {
    if (!url) return null;

    // Convert old Google Cloud Storage URLs to correct format
    return url.replace(
      'https://storage.googleapis.com/smartsaas-avatar-store/',
      'https://storage.cloud.google.com/smartsaas-avatar-store/'
    );
  };

 

  if (!calendars || calendars.length === 0) {
    console.log('📅 CalendarSelectionList: No calendars available!', {
      calendarsIsNull: calendars === null,
      calendarsIsUndefined: calendars === undefined,
      calendarsIsArray: Array.isArray(calendars),
      calendarsLength: calendars?.length
    });

    return (
      <View
        style={{
          backgroundColor: 'rgb(90,192,234)',
          borderRadius: 12,
          padding: 16,
          margin: 16,
          marginBottom: 8,
          borderWidth: 1,
          borderColor: theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
          zIndex: 1000,
          elevation: 10,
        }}
      >
        <Text
          style={{
            color: '#ffffff',
            textAlign: 'center',
            fontSize: 14,
          }}
        >
          {isLoading ? 'Loading calendars...' : 'No calendars available'}
        </Text>
      </View>
    );
  }

  return (
    <View
      style={{
        backgroundColor: 'rgb(90,192,234)',
        borderRadius: 12,
        marginBottom: 8,
        marginTop: 8,
        marginHorizontal: 30,
        top: -10,
        borderWidth: 1,
        borderColor: theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
        overflow: 'hidden',
        zIndex: 1000,
        elevation: 10,
      }}
    >
      {/* Header */}
      <Pressable onPress={handleToggle}>
        <XStack
          alignItems="center"
          justifyContent="space-between"
          padding={16}
          backgroundColor={theme ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)'}
        >
          <XStack alignItems="center" gap={12}>
            <MaterialIcons
              name="calendar-today"
              size={20}
              color="#ffffff"
            />
            <YStack>
              <Text
                style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: '#ffffff',
                }}
              >
                Calendars ({selectedCalendars.length}/{calendars.length})
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  color: 'rgba(255, 255, 255, 0.8)',
                }}
              >
                {selectedCalendars.length === calendars.length ? 'All selected' :
                  selectedCalendars.length === 0 ? 'None selected' :
                    `${selectedCalendars.length} selected`}
              </Text>
            </YStack>
          </XStack>
          <MaterialIcons
            name={isExpanded ? 'expand-less' : 'expand-more'}
            size={24}
            color="#ffffff"
          />
        </XStack>
      </Pressable>

      {/* Quick Actions */}
      {isExpanded && (
        <>
          <Separator />
          <XStack padding={16} gap={8}>
            <Pressable
              onPress={onSelectAll}
              style={{
                flex: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                padding: 8,
                borderRadius: 8,
                alignItems: 'center',
              }}
            >
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: '#ffffff',
                }}
              >
                Select All
              </Text>
            </Pressable>
            <Pressable
              onPress={onDeselectAll}
              style={{
                flex: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                padding: 8,
                borderRadius: 8,
                alignItems: 'center',
              }}
            >
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: '#ffffff',
                }}
              >
                Deselect All
              </Text>
            </Pressable>
          </XStack>
        </>
      )}

      {/* Calendar List */}
      {isExpanded && (
        <>
          <Separator />
          <ScrollView
            style={{ maxHeight: 300 }}
            showsVerticalScrollIndicator={false}
          >
            {calendars.map((calendar, index) => {
              const calendarId = calendar._id || calendar.calendarId || '';
              const calendarName = calendar.title || calendar.name || 'Unnamed Calendar';
              const isSelected = selectedCalendars.includes(calendarId);
              const calendarColor = getCalendarColor(calendar);
              const iconName = getCalendarIcon(calendar);

              const userName = getUserDisplayName(calendar.user);
              const avatarUrl = correctStorageUrl(calendar.user?.avatar);

              return (
                <View key={calendarId || index}>
                  <Pressable
                    onPress={() => onCalendarToggle(calendarId)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: 16,
                      backgroundColor: isSelected
                        ? 'rgba(255, 255, 255, 0.2)'
                        : 'transparent',
                    }}
                  >
                    {/* Color indicator */}
                    <View
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: calendarColor,
                        marginRight: 12,
                      }}
                    />

                    {/* User Avatar or Calendar Icon */}
                    {calendar.user && avatarUrl ? (
                      <Image
                        source={{ uri: avatarUrl }}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 16,
                          marginRight: 12,
                          borderWidth: 2,
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        }}
                        onError={() => console.log('Error loading avatar for', userName)}
                      />
                    ) : calendar.user ? (
                      <View
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 16,
                          marginRight: 12,
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderWidth: 2,
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        }}
                      >
                        <Text style={{ color: '#ffffff', fontSize: 14, fontWeight: '600' }}>
                          {userName?.charAt(0).toUpperCase()}
                        </Text>
                      </View>
                    ) : (
                      <MaterialIcons
                        name={iconName}
                        size={18}
                        color="rgba(255, 255, 255, 0.8)"
                        style={{ marginRight: 12 }}
                      />
                    )}

                    {/* Calendar info */}
                    <YStack flex={1}>
                      <Text
                        style={{
                          fontSize: 14,
                          fontWeight: isSelected ? '600' : '500',
                          color: '#ffffff',
                        }}
                      >
                        {calendarName}
                      </Text>
                      {userName && (
                        <Text
                          style={{
                            fontSize: 12,
                            color: 'rgba(255, 255, 255, 0.8)',
                            marginTop: 2,
                            fontWeight: '500',
                          }}
                        >
                          {userName}
                        </Text>
                      )}
                      {calendar.description && (
                        <Text
                          style={{
                            fontSize: 11,
                            color: 'rgba(255, 255, 255, 0.6)',
                            marginTop: 2,
                          }}
                        >
                          {calendar.description}
                        </Text>
                      )}
                    </YStack>

                    {/* QR Share Button */}
                    <Pressable
                      onPress={(e) => handleShowQR(calendar, e)}
                      style={{
                        padding: 8,
                        marginRight: 8,
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: 8,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <MaterialIcons
                        name="qr-code"
                        size={20}
                        color="#ffffff"
                      />
                    </Pressable>

                    {/* Selection indicator */}
                    <MaterialIcons
                      name={isSelected ? 'check-box' : 'check-box-outline-blank'}
                      size={20}
                      color={isSelected ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'}
                    />
                  </Pressable>
                  {index < calendars.length - 1 && (
                    <Separator
                      style={{
                        marginLeft: 40,
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      }}
                    />
                  )}
                </View>
              );
            })}
          </ScrollView>
        </>
      )}

      {/* QR Code Modal */}
      <CalendarQRModal
        ref={qrModalRef}
        calendar={selectedCalendar}
      />
    </View>
  );
};

export default CalendarSelectionList;
